/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {
  AccountLink,
  Entity
} from '@ion/core/src/types/entities';
import {
  DownloadTemplate
} from '@ion/core/src/types/templates';
import {
  AssetTemplateOptions,
  exportMapper
} from '@ion/core/src/xlsx/AssetTemplate';
import {BulkUploadDialog} from '@ion/dls/src/imports/BulkUploadDialog';
import {onImportStart} from './ImportAssetsContainerUtils';
import {importAdminAssets} from './state';

// defines the prop definitions for import devices container ...
export interface ImportAssetsContainerProps {
  onDownload: (params?: DownloadTemplate) => void;
  account?: AccountLink;
  overrideAssetTypeColumn?: boolean;
};

// defines the override styles ...
const overrides = () => ({
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  }
});

/**
 * This component acts as a container to initiate bulk device import ...
 */
export const ImportAssetsContainer = ({
  onDownload,
  account,
  overrideAssetTypeColumn = false
}: ImportAssetsContainerProps) => {
  const dispatch                              = useDispatch();
  const itemsRef                              = useRef();
  const importResults                         = useSelector(({importer: {current}}) => current);
  const [templateOptions, setTemplateOptions] = useState<AssetTemplateOptions>({
    groupName : account?.title,
    overrideAssetTypeColumn
  });

  const onClose = useCallback(() => {
    dispatch(importAdminAssets(false));
  }, []);

  const handleSubmit = useCallback(() => {
    onImportStart({
      items : itemsRef.current,
      account,
      dispatch
    });
  }, []);

  const itemsUpdate = useCallback((items) => {
    itemsRef.current = items;
  }, []);

  const handleDownload = useCallback((params?: DownloadTemplate) => {
    onDownload?.(params);
  }, []);

  const title = useMemo(() => {
    return translate('common:editors.import.title', {
      entity : translate('common:entityLabels.assets')
    });
  }, []);

  return (
    <BulkUploadDialog open
      dialogTitle={title}
      onClose={onClose}
      onCancel={onClose}
      styleOverrides={overrides}
      onImport={handleSubmit}
      onDownload={handleDownload}
      onConfirm={onClose}
      exportMapper={exportMapper}
      entity={Entity.asset}
      entityLabel={translate('common:entityLabels.assets')}
      templateSrcPath={'admin-assets'}
      templateOptions={templateOptions}
      isLoading={false}
      itemsUpdate={itemsUpdate}
      isDisabled={false}
      omitActions={false}
      importResults={importResults}
      itemsCount={itemsRef?.current?.length || 0}
      suppressResultsNotification />
  );
};

export default ImportAssetsContainer;
